import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { useWeeklyChallenge } from '@/hooks/useWeeklyChallenge';
import DuelIcon from '@/components/custom/icons/DuelIcon';
import checkIcon from './img/check-icon.svg';
import xIcon from './img/x-icon.svg';
import crownIcon from './img/crown-icon.png';
import crownAndLeaf from './img/crown_and_leaf.png';
import star from './img/star1.png';
import { EmptyState } from '@/components/custom/empty-state';
import { useSession } from '@/hooks/useSession';
import {
  ChallengeModalAvatar,
  ChallengeModalCrown,
  ChallengeModalDuel,
  ChallengeModalHat,
  ChallengeModalImgLeaf,
  ChallengeModalImgStar,
  ChallengeModalName,
  ChallengeModalNumber,
  ChallengeModalProfile,
  ChallengeModalResult,
  ChallengeModalResultBg,
  ChallengeModalResultEmpty,
  ChallengeModalResultItem,
  ChallengeModalResultItems,
  ChallengeModalResultSection,
  ChallengeModalResultTitle,
  ChallengeModalScore,
  ChallengeModalSection,
  ChallengeModalSectionNameSubtitle,
  ChallengeModalVs
} from '@/components/custom/challenge';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export function DuelsPlayerStepResult({ setNewDuelModal, winner, getWinner, handleCloseModal }) {
  const { currentRound, currentChallenge, setCurrentStep, setCurrentRound, currentStep } = useWeeklyChallenge();
  const [selectedTab, setSelectedTab] = useState(String(currentRound - 1));
  const [scores, setScores] = useState();

  const roundsCompletedByMe = scores?.['rounds_completed_by_me'] ?? 0;
  const totalRounds = currentChallenge?.['duel-rounds-count'];
  if (!Number.isFinite(totalRounds)) {
    throw new Error('currentChallenge sem duel-rounds-count');
  }
  const resultIndex = totalRounds;
  const tabs = [...Array(totalRounds)].map((_, i) => `Rodada ${i + 1}`).concat('Resultado');

  const { session } = useSession();
  const userAvatar = session?.user?.image?.url;
  const opponentAvatar = currentChallenge?.opponent?.image?.url;
  const client = useClient();

  const handleNextQuestion = () => {
    setCurrentStep(1);
    setCurrentRound(prev => prev + 1);
  };

  const getScore = async () => {
    const { data, error } = await client.fetch(`/duels/${currentChallenge?.id}/score`);

    if (error) {
      toast.error('Erro ao buscar resultados');
    } else {
      setScores(data);
    }
  };

  useEffect(() => {
    if (currentStep === 3) {
      getScore();
    }
  }, [currentStep]);

  // Pega vencedor quando atingir a última rodada ou quando duelo já estiver completed
  useEffect(() => {
    if ((currentChallenge?.status === 'completed' || currentRound === totalRounds) && !winner) {
      getWinner(currentChallenge?.id);
    }
  }, [currentChallenge?.status, currentRound, totalRounds, winner, currentChallenge?.id]);

  const roundsWithData = scores?.['scores_and_durations']?.length ?? 0;
  const currentScore = scores?.['scores_and_durations']?.[selectedTab];

  const creatorId = currentChallenge?.['creator-id']?.toString();
  const opponentId = currentChallenge?.['invited-id']?.toString();

  const creatorScore = currentScore?.answers?.[0]?.users?.[creatorId];
  const opponentScore = currentScore?.answers?.[0]?.users?.[opponentId];

  const formatDuration = durationInSeconds => {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <ChallengeModalSection>
      <ChallengeModalHat>
        {currentChallenge?.['parent-taxonomy']?.name}
        <ChallengeModalSectionNameSubtitle>
          <DuelIcon /> Duelo
        </ChallengeModalSectionNameSubtitle>
      </ChallengeModalHat>

      <ChallengeModalResult>
        {/* VENCEDOR DEFINIDO */}
        {Number(selectedTab) === resultIndex && winner && (
          <ChallengeModalResultBg>
            <ChallengeModalProfile>
              <ChallengeModalAvatar className="mb-2">
                <AvatarWrapper
                  size="lg"
                  src={winner?.['winner_id'] === session?.user?.id ? userAvatar : opponentAvatar}
                  text={winner?.['winner_id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}
                  className="size-[100px]"
                />
                <ChallengeModalImgLeaf
                  src={crownAndLeaf}
                  alt="coroa e folhas"
                />
                <ChallengeModalImgStar
                  variant={1}
                  src={star}
                  alt="estrela"
                />
                <ChallengeModalImgStar
                  variant={2}
                  src={star}
                  alt="estrela"
                />
                <ChallengeModalImgStar
                  variant={3}
                  src={star}
                  alt="estrela"
                />
                <ChallengeModalImgStar
                  variant={4}
                  src={star}
                  alt="estrela"
                />
              </ChallengeModalAvatar>
              <ChallengeModalName className="text-xs font-bold text-primary-dark">{winner?.['winner_id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}</ChallengeModalName>
              <Badge
                square
                className="border-2 border-solid border-white bg-primary-light px-2"
              >
                {winner?.['winner_score']?.toFixed(2)} pts
              </Badge>
            </ChallengeModalProfile>
          </ChallengeModalResultBg>
        )}

        {/* EXIBIÇÃO DOS PLACARES: FULANO X CICLANO */}
        {Number(selectedTab) !== resultIndex && (
          <ChallengeModalDuel>
            <ChallengeModalProfile>
              <ChallengeModalAvatar className="mb-4">
                <AvatarWrapper
                  size="md"
                  src={currentChallenge?.['creator-id'] === session?.user?.id ? userAvatar : opponentAvatar}
                  text={currentChallenge?.['creator-id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}
                />
                {opponentScore && creatorScore && creatorScore.correct && !opponentScore.correct && (
                  <ChallengeModalCrown
                    src={crownIcon}
                    alt="coroa"
                  />
                )}

                <Badge
                  square
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 border-2 border-solid border-white bg-neutral px-2"
                >
                  {creatorScore ? parseInt(creatorScore?.score) : '0'} pts
                </Badge>
              </ChallengeModalAvatar>
              <ChallengeModalName>{currentChallenge?.['creator-id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}</ChallengeModalName>
            </ChallengeModalProfile>

            <ChallengeModalScore>
              <ChallengeModalNumber>{scores?.rounds_won[creatorId] ?? 0}</ChallengeModalNumber>
              <ChallengeModalVs />
              <ChallengeModalNumber>{scores?.rounds_won[opponentId] ?? 0}</ChallengeModalNumber>
            </ChallengeModalScore>

            <ChallengeModalProfile>
              <ChallengeModalProfile>
                <ChallengeModalAvatar className="mb-4">
                  <AvatarWrapper
                    size="md"
                    src={currentChallenge?.['invited-id'] === session?.user?.id ? userAvatar : opponentAvatar}
                    text={currentChallenge?.['invited-id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}
                  />
                  {opponentScore && creatorScore && opponentScore.correct && !creatorScore.correct && (
                    <ChallengeModalCrown
                      src={crownIcon}
                      alt="coroa"
                    />
                  )}
                  <Badge
                    square
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 border-2 border-solid border-white bg-neutral px-2"
                  >
                    {opponentScore ? parseInt(opponentScore?.score) : '0'} pts
                  </Badge>
                </ChallengeModalAvatar>
              </ChallengeModalProfile>
              <ChallengeModalName>{currentChallenge?.['invited-id'] === session?.user?.id ? session?.user?.name : currentChallenge?.opponent?.name}</ChallengeModalName>
            </ChallengeModalProfile>
          </ChallengeModalDuel>
        )}

        <Tabs
          defaultValue={String(currentRound - 1)}
          value={selectedTab}
          onValueChange={setSelectedTab}
        >
          <TabsList className="!mb-0">
            {tabs.map((tab, index) => (
              <TabsTrigger
                key={index}
                value={String(index)}
                style={{ padding: '0.3em 0.7em' }}
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent
            value={selectedTab}
            className="mt-4"
          >
            {/* RESULTADO DA RODADA */}
            {Number(selectedTab) !== resultIndex && Number(selectedTab) > roundsWithData - 1 && (
              <ChallengeModalResultSection>
                <EmptyState
                  type="data"
                  text="Essa rodada ainda não começou"
                  variant="plain"
                />
              </ChallengeModalResultSection>
            )}

            {selectedTab !== resultIndex && selectedTab <= roundsWithData - 1 && (
              <div className="space-y-6">
                <ChallengeModalResultSection>
                  <ChallengeModalResultTitle>{currentScore?.answers?.[0]?.question?.content}</ChallengeModalResultTitle>
                  <ChallengeModalResultItems>
                    <ChallengeModalResultItem>
                      {creatorScore ? (
                        <>
                          <p className="!mb-0">
                            <img
                              src={creatorScore?.correct ? checkIcon : xIcon}
                              alt={`ícone de ${creatorScore?.correct ? 'correto' : 'errado'}`}
                            />
                            {creatorScore && !opponentScore && Number(creatorId) !== session?.user?.id ? 'Questão respondida' : creatorScore?.alternative_text}
                          </p>
                          <Badge variant="bright">{formatDuration(creatorScore?.duration)}</Badge>
                        </>
                      ) : (
                        <span>Aguardando resposta</span>
                      )}
                    </ChallengeModalResultItem>
                    <ChallengeModalResultItem>
                      {opponentScore ? (
                        <>
                          <p className="!mb-0">
                            <img
                              src={opponentScore?.correct ? checkIcon : xIcon}
                              alt={`ícone de ${opponentScore?.correct ? 'correto' : 'errado'}`}
                            />
                            {opponentScore && !creatorScore && Number(opponentId) !== session?.user?.id ? 'Questão respondida' : opponentScore?.alternative_text}
                          </p>
                          <Badge variant="bright">{formatDuration(opponentScore?.duration)}</Badge>
                        </>
                      ) : (
                        <span>Aguardando resposta</span>
                      )}
                    </ChallengeModalResultItem>
                  </ChallengeModalResultItems>
                </ChallengeModalResultSection>

                {roundsCompletedByMe < totalRounds && (
                  <>
                    <Button
                      fullWidth
                      onClick={handleNextQuestion}
                    >
                      Próxima rodada
                    </Button>
                    <Button
                      variant="link"
                      fullWidth
                      onClick={() => handleCloseModal(false)}
                    >
                      Continuar mais tarde
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* RESULTADOS GERAIS (VENCEDOR DEFINIDO) - ABA RESULTADOS */}
            {Number(selectedTab) === resultIndex && winner && (
              <>
                <ChallengeModalResultSection className="mb-4">
                  {scores?.['scores_and_durations']?.map((item, i) => (
                    <ChallengeModalResultItem
                      key={i}
                      hasBg
                    >
                      <h3>{item?.answers?.[0]?.question?.content}</h3>
                      <div class="grid grid-cols-2">
                        <div>
                          <p className="!mb-0">
                            <img
                              src={item?.answers?.[0]?.users?.[creatorId]?.correct === true ? checkIcon : xIcon}
                              alt="ícone de correto"
                            />
                            {item?.answers?.[0]?.users?.[creatorId]?.alternative_text}
                          </p>
                          <Badge
                            variant="bright"
                            size="sm"
                            className="ml-6"
                          >
                            {formatDuration(item?.answers?.[0]?.users?.[creatorId]?.duration)}
                          </Badge>
                        </div>
                        <div>
                          <p className="!mb-0">
                            <img
                              src={item?.answers?.[0]?.users?.[opponentId]?.correct === true ? checkIcon : xIcon}
                              alt="ícone de correto"
                            />
                            {item?.answers?.[0]?.users?.[opponentId]?.alternative_text}
                          </p>
                          <Badge
                            variant="bright"
                            size="sm"
                            className="ml-6"
                          >
                            {formatDuration(item?.answers?.[0]?.users?.[opponentId]?.duration)}
                          </Badge>
                        </div>
                      </div>
                    </ChallengeModalResultItem>
                  ))}
                </ChallengeModalResultSection>

                <Button
                  fullWidth
                  onClick={() => setNewDuelModal(true)}
                >
                  Novo Duelo
                </Button>
              </>
            )}

            {/* AINDA NÃO HÁ VENCEDOR - ABA RESULTADOS*/}
            {Number(selectedTab) === resultIndex && !winner && (
              <ChallengeModalResultBg className="h-[300px]">
                <ChallengeModalResultEmpty>?</ChallengeModalResultEmpty>
              </ChallengeModalResultBg>
            )}
          </TabsContent>
        </Tabs>
      </ChallengeModalResult>
    </ChallengeModalSection>
  );
}
